<script lang="ts">
	import { Checkbox } from '../../../../../../components/Checkbox';
	import type { CheckboxValue } from '../../../../../../components/Checkbox/types';
	import { InputLabel } from '../../../../../../components/InputLabel';
	import { filterStores } from '../../../../state';
	import type { FilterCheckboxList, FilterDrawerState } from '../../../../types';
	import { getFilterCollectionValue } from '../../../../utils/getFilterCollectionValue/getFilterCollectionValue';
	import { setFilterCollectionValue } from '../../../../utils/setFilterCollectionValue/setFilterCollectionValue';

	interface Props {
		filterCollectionId: string;
		filterCheckboxList: FilterCheckboxList;
	}

	let { filterCollectionId, filterCheckboxList }: Props = $props();

	const { filterStore } = filterStores;

	let hasSelectAll = $derived(filterCheckboxList?.select_all);

	let filterValue = $derived(
		getFilterCollectionValue<string[]>({
			filterCollectionId,
			filter: $filterStore as FilterDrawerState
		})
	);

	let checkboxes = $derived(filterCheckboxList?.filter_checkboxes);

	let values = $derived(
		checkboxes?.reduce(
			(acc, checkbox) => {
				if (!checkbox?.id || !checkbox?.value) return acc;

				return {
					...acc,
					[checkbox.id]: !!filterValue?.includes?.(checkbox.value)
				};
			},
			{} as Record<string, boolean>
		) || {}
	);

	const getSelectAll = ({
		filterValueLength,
		checkboxesLength
	}: {
		filterValueLength: number;
		checkboxesLength: number;
	}): CheckboxValue => {
		if (filterValueLength === checkboxesLength) return true;

		if (filterValueLength === 0) return false;

		return 'indeterminate';
	};

	let selectAll = $derived(
		getSelectAll({
			filterValueLength: filterValue?.length || 0,
			checkboxesLength: checkboxes?.length || 0
		})
	);

	let dataCyPrefix = $derived(`filter-drawer-checkboxes-id-${filterCollectionId}`);

	const handleSelectAllChange = (checked: CheckboxValue) => {
		if (checked && checkboxes) {
			const newValue = checkboxes.reduce((acc, checkbox) => {
				if (!checkbox?.value) return acc;

				return [...acc, checkbox.value];
			}, [] as string[]);

			setFilterCollectionValue({
				filterCollectionId,
				value: newValue
			});

			return;
		}

		setFilterCollectionValue({
			filterCollectionId,
			value: []
		});
	};

	const handleChange =
		({ value }: { value: string }) =>
		(checked: CheckboxValue) => {
			if (!values) return;

			const newValue = checked
				? [...filterValue, value]
				: filterValue.filter((val) => val !== value);

			setFilterCollectionValue({
				filterCollectionId,
				value: newValue
			});
		};

	const getCheckboxHtmlId = ({ id }: { id: string }) => {
		return `filter-${filterCollectionId}-${id}`;
	};
</script>

<!-- TODO filter drawer : adjust data-cy -->
{#if checkboxes}
	<div class="flex w-full flex-col gap-4">
		{#if hasSelectAll}
			<InputLabel dataCy={`${dataCyPrefix}-all`} for={getCheckboxHtmlId({ id: 'all' })}
				><Checkbox
					dataCy={`${dataCyPrefix}-all-label`}
					id={getCheckboxHtmlId({ id: 'all' })}
					onChange={handleSelectAllChange}
					checked={selectAll}
				/> All</InputLabel
			>
		{/if}

		{#each checkboxes as checkbox}
			{#if checkbox?.id && checkbox.value}
				<InputLabel dataCy={dataCyPrefix} for={getCheckboxHtmlId({ id: checkbox.id })}
					><Checkbox
						dataCy={`${dataCyPrefix}-label`}
						id={getCheckboxHtmlId({ id: checkbox.id })}
						checked={values[checkbox.id]}
						onChange={handleChange({
							value: checkbox.value
						})}
					/>

					{checkbox?.label}
				</InputLabel>
			{/if}
		{/each}
	</div>
{/if}
